import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Camera, Plus, Send, ExternalLink, FilePlus2, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Badge, Label, sel, SlotPicker, TYPE_META, STATUS_META, PUB_META,
  HL_TYPES, HL_STATUS_META, fmtD, fmtDur, TODAY, PubProgressBar, PUB_SEGMENTS,
  EVENT_STAGES, eventStageKey, isCreated,
} from "./shared";

/* Panneau latéral d'un temps fort : tout est modifiable sans quitter la page */
export function HighlightDrawer({ highlightId, shootings, onClose, onChanged }) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [f, setF] = useState(null);
  const [comment, setComment] = useState("");
  const [newItem, setNewItem] = useState({ title: "", type: "reel", shooting_needed: false });
  const [attachId, setAttachId] = useState("");
  const [newSlot, setNewSlot] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/content-piloting/highlights/${highlightId}/detail`).then((r) => {
      setDetail(r.data);
      const h = r.data.highlight;
      setF({
        name: h.name || "", type: h.type || "evenement", status: h.status || "a_preparer",
        event_date: h.event_date || "", production_deadline: h.production_deadline || "",
        diffusion_date: h.diffusion_date || "", month: h.month || "",
        responsible: h.responsible || "", objective: h.objective || "", offer: h.offer || "",
        description: h.description || "", notes: h.notes || "",
      });
    }).catch((e) => { toast.error(apiError(e)); onClose(); });
  }, [highlightId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  if (!detail || !f) {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
        <div className="bg-white w-full max-w-lg h-full p-10 text-center" onClick={(e) => e.stopPropagation()}>
          <div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  const h = detail.highlight;
  const items = detail.items || [];
  const createdCount = items.filter(isCreated).length;
  const futureShootings = (shootings || []).filter((s) => s.status !== "annule" && s.date >= TODAY);

  const act = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); load(); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const save = async () => {
    setBusy(true);
    await act(() => api.patch(`/content-piloting/highlights/${highlightId}`, f), "Temps fort enregistré");
    setBusy(false);
  };
  const addItem = () => {
    if (!newItem.title.trim()) { toast.error("Titre requis"); return; }
    act(() => api.post("/content-piloting/items", {
      client_id: h.client_id, highlight_id: highlightId, title: newItem.title,
      type: newItem.type, shooting_needed: newItem.shooting_needed,
    }), "Contenu ajouté à la checklist").then(() => setNewItem({ title: "", type: "reel", shooting_needed: false }));
  };
  const attachExisting = () => {
    if (!attachId) { toast.error("Choisissez un shooting"); return; }
    act(() => api.post(`/content-piloting/shootings/${attachId}/attach-highlight`, { highlight_id: highlightId }),
      "Événement ajouté au shooting — contenus à tourner rattachés");
  };
  const bookNew = () => {
    if (!newSlot) { toast.error("Choisissez un créneau disponible"); return; }
    act(async () => {
      const s = await api.post("/content-piloting/shootings", {
        client_id: h.client_id, date: newSlot.date, time: newSlot.start, duration_min: 120,
      });
      await api.post(`/content-piloting/shootings/${s.data.id}/attach-highlight`, { highlight_id: highlightId });
    }, "Shooting planifié et bloqué dans l'agenda");
  };
  const addComment = () => {
    if (!comment.trim()) return;
    act(() => api.post(`/content-piloting/highlights/${highlightId}/comments`, { text: comment }), null)
      .then(() => setComment(""));
  };

  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-5 space-y-4 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="hl-drawer">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <Input value={f.name} onChange={set("name")} className="font-heading font-bold text-lg" data-testid="hl-drawer-name" />
            <p className="text-[11px] text-neutral-400 mt-1 flex items-center gap-2">{HL_TYPES[f.type]}{h.origin === "client" ? " · proposé par le client" : ""} <Badge meta={EVENT_STAGES} value={eventStageKey(items)} /></p>
          </div>
          <button onClick={onClose} className="p-1 text-neutral-400 hover:text-neutral-700" data-testid="hl-drawer-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <div><Label>Type</Label>
            <select className={sel} value={f.type} onChange={set("type")} data-testid="hl-drawer-type">
              {Object.entries(HL_TYPES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
          <div><Label>Statut</Label>
            <select className={sel} value={f.status} onChange={set("status")} data-testid="hl-drawer-status">
              {Object.entries(HL_STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
            </select></div>
          <div><Label>Date de l'événement</Label><Input type="date" value={f.event_date} onChange={set("event_date")} data-testid="hl-drawer-event-date" /></div>
          <div><Label>Mois de pilotage</Label><Input type="month" value={f.month} onChange={set("month")} data-testid="hl-drawer-month" /></div>
          <div><Label>Date limite de production</Label><Input type="date" value={f.production_deadline} onChange={set("production_deadline")} data-testid="hl-drawer-deadline" /></div>
          <div><Label>Diffusion souhaitée</Label><Input type="date" value={f.diffusion_date} onChange={set("diffusion_date")} data-testid="hl-drawer-diffusion" /></div>
          <div><Label>Responsable</Label><Input value={f.responsible} onChange={set("responsible")} data-testid="hl-drawer-responsible" /></div>
          <div><Label>Offre commerciale</Label><Input value={f.offer} onChange={set("offer")} data-testid="hl-drawer-offer" /></div>
        </div>
        <div><Label>Objectif de communication</Label><Input value={f.objective} onChange={set("objective")} data-testid="hl-drawer-objective" /></div>
        <div><Label>Description</Label><Textarea rows={2} value={f.description} onChange={set("description")} data-testid="hl-drawer-description" /></div>
        <Button className="w-full bg-[#0B132B] hover:bg-[#141E3C] text-white" disabled={busy} onClick={save} data-testid="hl-drawer-save">
          {busy ? "Enregistrement…" : "Enregistrer les modifications"}
        </Button>

        {/* Progression de production — case « Contenu créé » indépendante du statut */}
        <div className="border border-neutral-200 rounded-xl p-3 space-y-2" data-testid="hl-drawer-checklist">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-neutral-600">Contenus à créer</p>
            <p className="text-[11px] text-neutral-400" data-testid="hl-drawer-progress">{createdCount}/{items.length} créés</p>
          </div>
          <PubProgressBar items={items} testid="hl-pub-progress" />
          <p className="text-[9px] text-neutral-400" data-testid="hl-pub-legend">
            Publications : {PUB_SEGMENTS(items).green} validée(s) · {PUB_SEGMENTS(items).orange} à valider · {PUB_SEGMENTS(items).gray} à créer
          </p>
          <div className="divide-y divide-neutral-100">
            {items.map((i) => (
              <div key={i.id} className="py-2 flex items-center gap-2" data-testid={`hl-item-${i.id}`}>
                <input type="checkbox" className="rounded accent-emerald-600 shrink-0" checked={!!i.created}
                  data-testid={`hl-item-created-${i.id}`}
                  onChange={(e) => act(() => api.patch(`/content-piloting/items/${i.id}`, { created: e.target.checked }),
                    e.target.checked ? "Contenu créé ✓" : "Coche retirée")} />
                <div className="min-w-0 flex-1">
                  <p className={`text-xs font-medium truncate ${isCreated(i) ? "line-through text-neutral-400" : ""}`}>{i.title}</p>
                  <p className="text-[10px] text-neutral-400">
                    Limite : {fmtD(i.creation_deadline)}{i.responsible ? ` · ${i.responsible}` : ""}
                    {i.shot ? " · tourné ✓" : ""}
                  </p>
                </div>
                <select className="border border-neutral-200 rounded-lg px-1.5 py-1 text-[10px] bg-white shrink-0"
                  value={i.status} data-testid={`hl-item-status-${i.id}`}
                  onChange={(e) => act(() => api.patch(`/content-piloting/items/${i.id}`, { status: e.target.value }), "Statut mis à jour")}>
                  {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
                </select>
                <Badge meta={TYPE_META} value={i.type} />
                <button className="p-1 text-neutral-300 hover:text-red-500 transition-colors shrink-0" title="Supprimer ce contenu"
                  data-testid={`hl-item-del-${i.id}`}
                  onClick={() => {
                    if (!window.confirm(`Supprimer le contenu « ${i.title} » ?${i.publication_id ? " La publication liée restera dans le calendrier éditorial." : ""}`)) return;
                    act(() => api.delete(`/content-piloting/items/${i.id}`), "Contenu supprimé");
                  }}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                {i.publication_id ? (
                  <button className={`text-[10px] font-medium flex items-center gap-0.5 shrink-0 ${PUB_META[i.publication_status]?.cls}`}
                    onClick={() => navigate(`/publications/${i.publication_id}`)} data-testid={`hl-item-pub-${i.id}`}>
                    {PUB_META[i.publication_status]?.label} <ExternalLink className="w-2.5 h-2.5" />
                  </button>
                ) : (
                  <button className="text-[10px] font-medium text-blue-600 hover:text-blue-800 flex items-center gap-0.5 shrink-0"
                    data-testid={`hl-item-pub-create-${i.id}`}
                    onClick={() => act(async () => {
                      const r = await api.post(`/content-piloting/items/${i.id}/create-publication`, {});
                      navigate(`/publications/${r.data.id}`);
                    }, "Publication créée dans le calendrier éditorial et liée")}>
                    <FilePlus2 className="w-3 h-3" /> Créer la publication
                  </button>
                )}
              </div>
            ))}
            {items.length === 0 && <p className="text-[11px] text-neutral-300 py-3 text-center">Aucun contenu — ajoutez le premier ci-dessous.</p>}
          </div>
          <div className="flex gap-1.5 pt-1">
            <Input className="h-8 text-xs flex-1" placeholder="Nouveau contenu à produire…" value={newItem.title}
              onChange={(e) => setNewItem({ ...newItem, title: e.target.value })} data-testid="hl-new-item-title" />
            <select className="border border-neutral-200 rounded-lg px-1.5 text-xs bg-white" value={newItem.type}
              onChange={(e) => setNewItem({ ...newItem, type: e.target.value })} data-testid="hl-new-item-type">
              {Object.entries(TYPE_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
            </select>
            <label className="flex items-center gap-1 text-[10px] text-neutral-500 whitespace-nowrap" data-testid="hl-new-item-shooting-wrap">
              <input type="checkbox" className="rounded" checked={newItem.shooting_needed}
                onChange={(e) => setNewItem({ ...newItem, shooting_needed: e.target.checked })} data-testid="hl-new-item-shooting" />
              À tourner
            </label>
            <Button size="sm" className="h-8 text-xs bg-[#0B132B] text-white" onClick={addItem} data-testid="hl-new-item-add"><Plus className="w-3.5 h-3.5" /></Button>
          </div>
        </div>

        {/* Shooting associé */}
        <div className="border border-neutral-200 rounded-xl p-3 space-y-2" data-testid="hl-drawer-shooting">
          <p className="text-xs font-semibold text-neutral-600 flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" /> Shooting</p>
          {detail.shooting ? (
            <p className="text-xs text-neutral-600" data-testid="hl-drawer-shooting-info">
              Programmé le <strong>{fmtD(detail.shooting.date)}</strong>{detail.shooting.time ? ` à ${detail.shooting.time}` : ""}
              {detail.shooting.duration_min ? ` (${fmtDur(detail.shooting.duration_min)})` : ""}
              {detail.shooting.location ? ` — ${detail.shooting.location}` : ""}
            </p>
          ) : (
            <div className="space-y-2">
              <div className="flex gap-1.5">
                <select className={`${sel} !py-1.5 text-xs flex-1`} value={attachId} onChange={(e) => setAttachId(e.target.value)} data-testid="hl-attach-select">
                  <option value="">Rattacher à un shooting existant…</option>
                  {futureShootings.map((s) => (
                    <option key={s.id} value={s.id}>{fmtD(s.date)}{s.time ? ` ${s.time}` : ""}{s.capacity ? ` (${(s.items || []).length}/${s.capacity})` : ""}</option>
                  ))}
                </select>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={attachExisting} data-testid="hl-attach-btn">Ajouter</Button>
              </div>
              <SlotPicker endpoint="/content-piloting/shooting-slots" picked={newSlot} onPick={setNewSlot} testid="hl-slot-picker" />
              {newSlot && (
                <Button size="sm" className="w-full h-8 text-xs bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" onClick={bookNew} data-testid="hl-book-new">
                  Planifier le shooting le {fmtD(newSlot.date)} à {newSlot.start}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Notes & commentaires */}
        <div><Label>Notes internes</Label><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="hl-drawer-notes" /></div>
        <div className="border border-neutral-200 rounded-xl p-3 space-y-2" data-testid="hl-drawer-comments">
          <p className="text-xs font-semibold text-neutral-600">Commentaires</p>
          {(h.comments || []).map((c) => (
            <div key={c.id} className="text-xs bg-neutral-50 rounded-lg px-2.5 py-1.5" data-testid={`hl-comment-${c.id}`}>
              <span className="font-medium">{c.author}</span> <span className="text-neutral-300">· {new Date(c.created_at).toLocaleDateString("fr-FR")}</span>
              <p className="text-neutral-600 mt-0.5">{c.text}</p>
            </div>
          ))}
          {(h.comments || []).length === 0 && <p className="text-[11px] text-neutral-300">Aucun commentaire.</p>}
          <div className="flex gap-1.5">
            <Input className="h-8 text-xs flex-1" placeholder="Ajouter un commentaire…" value={comment}
              onChange={(e) => setComment(e.target.value)} data-testid="hl-comment-input" />
            <Button size="sm" variant="outline" className="h-8" onClick={addComment} data-testid="hl-comment-send"><Send className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
